import React, { useEffect, useState, useMemo } from "react";
import withClinic from "../hooks/with_clinic";
import { ApolloResult, Clinic, ServiceHelper, CheckIn } from "../store";
import {
  Row,
  Col,
  Typography,
  Empty,
  Button,
  Popconfirm,
  InputNumber,
  Input,
  Switch,
  Card,
} from "antd";
import Search from "antd/lib/input/Search";
import { Form } from "antd";
import { Table, message } from "antd";
import { format, subDays, addDays } from "date-fns";
import DateRange from "../components/date_range";
import ButtonGroup from "antd/lib/button/button-group";
import { useMutation, useQuery } from "@apollo/client";
import LoadingSpinner from "../components/loading_indicator";
import Error from "../components/apollo_error";
import {
  LoadingOutlined,
  EditOutlined,
  SaveOutlined,
  PlusCircleOutlined,
  FileExcelFilled,
} from "@ant-design/icons";
import {
  helpers,
  getHelpersVar,
  create_one_helper,
  update_one_helper,
  getUpdateOneHelperVar,
} from "../graphql/helper";
import paths from "../routes/paths";
import { Link } from "react-router-dom";
import { boolean } from "yup";
import client from "../graphql/apollo_client";
import { get_practitioner_checkins, getHelperCheckInsVar, get_checkins_count } from "../graphql/checkin";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";

const { TextArea } = Input;

interface P {
  currentClinic?: Clinic;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text" | "switch" | "textarea";
  record: ServiceHelper;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  let inputNode =
    inputType === "number" ? (
      <InputNumber />
    ) : inputType === "switch" ? (
      <Switch defaultChecked={record.status === "ACTIVE"} />
    ) : (
      <Input />
    );
  if (inputType === "textarea") {
    inputNode = (
      <TextArea
        key={`${dataIndex}`}
        rows={3}
        defaultValue={(record as any)[dataIndex]}
      />
    );
  }
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const HelperPage = (props: P) => {
  const clinicId = props.currentClinic?.id || "";
  // const clinicId = "clhobuntc0002s6019ajmqkcj";
  const [searchText, setSearchText] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [serviceHelper, setServiceHelper] = useState<ServiceHelper[]>([]);
  const [editingKey, setEditingKey] = useState("");
  const [exporting, setExporting] = useState(false);
  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 30),
    endDate: addDays(new Date(), 15),
  });
  const [helperDetailsPagination, setHelperDetailsPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const isEditing = (record: ServiceHelper) => record.id === editingKey;
  const variables = getHelpersVar(clinicId);
  const { loading, data, error, refetch } = useQuery<
    ApolloResult<"serviceHelpers", ServiceHelper[]>
  >(helpers, { variables });

  const [update, { loading: saveLoading }] =
    useMutation<ApolloResult<"updateOneServiceHelper", ServiceHelper>>(
      update_one_helper
    );
  const [create, { loading: createLoading }] =
    useMutation<ApolloResult<"createOneServiceHelper", ServiceHelper>>(
      create_one_helper
    );

  const helperDetailsVariables = useMemo(
    () =>
      getHelperCheckInsVar({
        clinicId,
        startDate: ranges.startDate,
        endDate: ranges.endDate,
        take: helperDetailsPagination.pageSize,
        skip: (helperDetailsPagination.current - 1) * helperDetailsPagination.pageSize,
      }),
    [clinicId, ranges.startDate, ranges.endDate, helperDetailsPagination.current, helperDetailsPagination.pageSize]
  );
  const { 
    loading: helperDetailsLoading, 
    data: helperDetailsData, 
    error: helperDetailsError 
  } = useQuery<ApolloResult<"checkIns", CheckIn[]>>(get_practitioner_checkins, {
    variables: helperDetailsVariables,
    skip: !clinicId,
  });

  // Query for total count of helper details
  const helperDetailsCountVariables = useMemo(
    () => ({
      where: helperDetailsVariables.where,
    }),
    [helperDetailsVariables.where]
  );
  const { data: helperDetailsCountData } = useQuery<{
    aggregateCheckIn: { _count: { _all: number } };
  }>(get_checkins_count, {
    variables: helperDetailsCountVariables,
    skip: !clinicId,
  });

  useEffect(() => {
    if (data?.serviceHelpers) {
      setServiceHelper(data.serviceHelpers);
    }
  }, [data]);

  if (loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (!data) return <Empty />;

  const edit = (record: ServiceHelper) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id!);
  };

  const add = () => {
    const newData: ServiceHelper = {
      id: `new_${new Date().getTime()}`,
      name: "New Helper",
      status: "INACTIVE",
      created_at: new Date(),
      services: [],
      sort_order: 1,
    };
    const newList = [newData, ...serviceHelper];
    setServiceHelper(newList);
    setEditingKey(newData.id);
  };

  const save = async (record: ServiceHelper) => {
    if (record.id.startsWith("new_")) {
      await onCreate(record);
    } else {
      let formData = (await form.validateFields()) as {
        status: string | boolean;
      };
      // console.log(formData.status);
      const status =
        typeof formData.status === "boolean"
          ? formData.status
          : formData.status === "ACTIVE";

      const updatedData = {
        ...formData,
        status: status ? "ACTIVE" : "INACTIVE",
      } as ServiceHelper;
      const result = await update({
        variables: getUpdateOneHelperVar(record.id, updatedData),
      });
      // console.log(result);

      if (result.data?.updateOneServiceHelper) {
        message.success("Update successful");

        await refetch();
      } else {
        message.error("Update failed");
      }
    }
    form.resetFields();
    setEditingKey("");
  };

  const onCreate = async (record: ServiceHelper) => {
    let formData = (await form.validateFields()) as { status: boolean };
    const newData = {
      ...formData,
      status: formData.status === true ? "ACTIVE" : "INACTIVE",
    } as any;
    delete newData["id"];
    const result = await create({
      variables: {
        data: {
          ...newData,
          clinic: {
            connect: {
              id: clinicId,
            },
          },
        },
      },
    });

    if (result.data?.createOneServiceHelper) {
      message.success("Create successful");
      await refetch();
    } else {
      message.error("Create failed");
    }
  };

  const onCancel = (record: ServiceHelper) => {
    if (record.id.startsWith("new_")) {
      const newList = serviceHelper.filter((item) => item.id !== record.id);
      setServiceHelper(newList);
    }
    form.resetFields();
    setEditingKey("");
  };

  const columns: any[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      editable: true,
      filteredValue: searchText ? [searchText] : null,
      sorter: (a: ServiceHelper, b: ServiceHelper) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      onFilter: (value: string, record: ServiceHelper) =>
        record.name.toLowerCase().includes(value.toLowerCase()),
      render: (v: ServiceHelper, r: ServiceHelper) => (
        <Link to={paths.getServiceHelperDetailRoute(r.id)}>{r.name}</Link>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a: ServiceHelper, b: ServiceHelper) =>
        a.status.localeCompare(b.status),
      editable: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sorter: (a: ServiceHelper, b: ServiceHelper) =>
        a.status.localeCompare(b.status),
      editable: true,
    },
    {
      title: "Sort Order",
      dataIndex: "sort_order",
      key: "sort_order",
      editable: true,
      sorter: (a: ServiceHelper, b: ServiceHelper) =>
        a.sort_order - b.sort_order,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      editable: false,
      sorter: (a: ServiceHelper, b: ServiceHelper) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (text: string, record: ServiceHelper) =>
        format(new Date(record.created_at), "dd MMM, yyyy"),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: "10%",
      dataIndex: "action",
      editable: false,
      fixed: "right",
      render: (text: string, record: ServiceHelper) => {
        const editable = isEditing(record);
        return (
          <ButtonGroup>
            {editable ? (
              saveLoading || createLoading ? (
                <Button type="primary" icon={<LoadingOutlined />}>
                  Saving...
                </Button>
              ) : (
                <Popconfirm
                  placement="topRight"
                  title={`Sure to save ${record.name}?`}
                  onConfirm={() => save(record)}
                  onCancel={() => onCancel(record)}
                >
                  <Button type="primary" icon={<SaveOutlined />}>
                    Save
                  </Button>
                </Popconfirm>
              )
            ) : (
              <Button
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => edit(record)}
              />
            )}
          </ButtonGroup>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: ServiceHelper) => ({
        record,
        inputType:
          col.dataIndex === "sort_order"
            ? "number"
            : col.dataIndex === "status"
            ? "switch"
            : col.dataIndex === "description"
            ? "textarea"
            : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const helperDetailsColumns: any[] = [
    {
      title: "Helper Name",
      dataIndex: "practitioners",
      key: "helper_name",
      sorter: (a: CheckIn, b: CheckIn) => {
        const getHelperNames = (checkIn: CheckIn): string => {
          if (checkIn.practitioners && checkIn.practitioners.length > 0) {
            const helpers = checkIn.practitioners
              .filter((p) => p.helper_id && p.helper)
              .map((p) => p.helper?.name)
              .filter((name): name is string => !!name)
              .join(", ");
            if (helpers) return helpers;
          }
          return checkIn.helper?.name || "";
        };
        const aNames = getHelperNames(a);
        const bNames = getHelperNames(b);
        return aNames.toLowerCase().localeCompare(bNames.toLowerCase());
      },
      render: (v: any, r: CheckIn) => {
        if (r.practitioners && r.practitioners.length > 0) {
          const helpers = r.practitioners
            .filter((p) => p.helper_id && p.helper)
            .map((p) => p.helper?.name)
            .filter((name): name is string => !!name)
            .join(", ");
          if (helpers) return helpers;
        }
        // Fallback to direct helper from check-in
        return r.helper?.name || "-";
      },
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      sorter: (a: CheckIn, b: CheckIn) =>
        (a.service?.name || "")
          .toLowerCase()
          .localeCompare((b.service?.name || "").toLowerCase()),
      render: (v: any, r: CheckIn) => {
        return r.service?.name || "-";
      },
    },
    {
      title: "Customer",
      dataIndex: "member",
      key: "member",
      sorter: (a: CheckIn, b: CheckIn) =>
        (a.member?.name || "")
          .toLowerCase()
          .localeCompare((b.member?.name || "").toLowerCase()),
      render: (v: any, r: CheckIn) => {
        if (r.member?.clinic_members && r.member.clinic_members.length > 0) {
          return r.member.clinic_members[0].name;
        }
        return r.member?.name || "-";
      },
    },
    {
      title: "Practitioner",
      dataIndex: "practitioner",
      key: "practitioner",
      sorter: (a: CheckIn, b: CheckIn) => {
        const getPractitionerNames = (checkIn: CheckIn): string => {
          if (checkIn.practitioners && checkIn.practitioners.length > 0) {
            const practitioners = checkIn.practitioners
              .filter((p) => p.practitioner_id && p.practitioner)
              .map((p) => p.practitioner?.name)
              .filter((name): name is string => !!name)
              .join(", ");
            if (practitioners) return practitioners;
          }
          return checkIn.practitioner?.name || "";
        };
        const aNames = getPractitionerNames(a);
        const bNames = getPractitionerNames(b);
        return aNames.toLowerCase().localeCompare(bNames.toLowerCase());
      },
      render: (v: any, r: CheckIn) => {
        if (r.practitioners && r.practitioners.length > 0) {
          const practitioners = r.practitioners
            .filter((p) => p.practitioner_id && p.practitioner)
            .map((p) => p.practitioner?.name)
            .filter((name): name is string => !!name)
            .join(", ");
          if (practitioners) return practitioners;
        }
        // Fallback to direct practitioner from check-in
        return r.practitioner?.name || "-";
      },
    },
    {
      title: "Date",
      dataIndex: "in_time",
      key: "in_time",
      sorter: (a: CheckIn, b: CheckIn) =>
        new Date(a.in_time).getTime() - new Date(b.in_time).getTime(),
      render: (v: number, r: CheckIn) => {
        return format(new Date(r.in_time), "dd MMM,yyyy h:mm a");
      },
    },
  ];

  const handleHelperDetailsPaginationChange = (page: number, pageSize?: number) => {
    setHelperDetailsPagination({
      current: page,
      pageSize: pageSize || helperDetailsPagination.pageSize,
    });
  };

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
    // Reset helper details pagination when date range changes
    setHelperDetailsPagination({ current: 1, pageSize: helperDetailsPagination.pageSize });
  };

  const helperDetailsTable = (
    <Card title="Appointment Details" style={{ marginBottom: 16 }}>
      {helperDetailsError ? (
        <Error error={helperDetailsError} />
      ) : (
        <Table
          loading={helperDetailsLoading}
          showHeader={(helperDetailsData?.checkIns || []).length > 0}
          dataSource={(helperDetailsData?.checkIns || []).map((c) => ({
            key: c.id,
            ...c,
          }))}
          columns={helperDetailsColumns}
          pagination={{
            current: helperDetailsPagination.current,
            pageSize: helperDetailsPagination.pageSize,
            total: helperDetailsCountData?.aggregateCheckIn?._count?._all || 0,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: handleHelperDetailsPaginationChange,
            onShowSizeChange: handleHelperDetailsPaginationChange,
          }}
        />
      )}
    </Card>
  );

  const handleExport = async () => {
    const fileName =
      "service_helpers_" +
      format(ranges.startDate, "yyyy-MMM-dd") +
      "_" +
      format(ranges.endDate, "yyyy-MMM-dd");
    let excelData: any[] = [];

    const header = [
      "Name",
      "Status",
      "Description",
      "Sort Order",
      "Created At",
    ];

    setExporting(true);
    try {
      const variables = getHelpersVar(clinicId);
      const result = await client.query<
        ApolloResult<"serviceHelpers", ServiceHelper[]>
      >({
        query: helpers,
        variables,
      });

      const helperList = result.data?.serviceHelpers || [];

      // Format data for Excel - Helpers
      for (const helper of helperList) {
        const name = helper.name;
        const status = helper.status;
        const description = helper.description || "";
        const sortOrder = helper.sort_order;
        const createdAt = format(new Date(helper.created_at), "dd MMM,yyyy");

        excelData.push({
          name,
          status,
          description,
          sort_order: sortOrder,
          created_at: createdAt,
        });
      }

      // Fetch all helper details (check-ins with helpers) in batches for export
      let helperDetailsList: CheckIn[] = [];
      let helperDetailsRolling = true;
      const helperDetailsTake = 100;
      let helperDetailsSkip = 0;
      while (helperDetailsRolling) {
        const helperDetailsVar = getHelperCheckInsVar({
          clinicId,
          startDate: ranges.startDate,
          endDate: ranges.endDate,
          take: helperDetailsTake,
          skip: helperDetailsSkip,
        });
        const helperDetailsResult = await client.query<
          ApolloResult<"checkIns", CheckIn[]>
        >({
          query: get_practitioner_checkins,
          variables: helperDetailsVar,
        });
        const helperDetails = helperDetailsResult.data?.checkIns || [];
        helperDetailsList = helperDetailsList.concat(helperDetails);
        helperDetailsSkip += helperDetails.length;
        helperDetailsRolling = helperDetails.length === helperDetailsTake;
      }

      // Format helper details data for Excel
      const helperDetailsHeader = [
        "Helper Name",
        "Service",
        "Customer",
        "Practitioner",
        "Date",
      ];
      const helperDetailsExcelData = helperDetailsList.map((c) => {
        let helperName = "-";
        if (c.practitioners && c.practitioners.length > 0) {
          const helpers = c.practitioners
            .filter((p) => p.helper_id && p.helper)
            .map((p) => p.helper?.name)
            .filter((name): name is string => !!name)
            .join(", ");
          if (helpers) {
            helperName = helpers;
          } else {
            // Fallback to direct helper from check-in
            helperName = c.helper?.name || "-";
          }
        } else {
          // Fallback to direct helper from check-in
          helperName = c.helper?.name || "-";
        }
        const service = c.service?.name || "-";
        const customer =
          c.member?.clinic_members && c.member.clinic_members.length > 0
            ? c.member.clinic_members[0].name
            : c.member?.name || "-";
        let practitioner = "-";
        if (c.practitioners && c.practitioners.length > 0) {
          const practitioners = c.practitioners
            .filter((p) => p.practitioner_id && p.practitioner)
            .map((p) => p.practitioner?.name)
            .filter((name): name is string => !!name)
            .join(", ");
          if (practitioners) {
            practitioner = practitioners;
          } else {
            practitioner = c.practitioner?.name || "-";
          }
        } else {
          practitioner = c.practitioner?.name || "-";
        }
        const date = format(new Date(c.in_time), "dd MMM,yyyy h:mm a");

        return {
          helper_name: helperName,
          service,
          customer,
          practitioner,
          date,
        };
      });

      // Create worksheets
      const helpersWs = XLSX.utils.json_to_sheet(excelData);
      const helperDetailsWs = XLSX.utils.json_to_sheet(helperDetailsExcelData);

      // Add headers
      if (header != null && header.length > 0) {
        XLSX.utils.sheet_add_aoa(helpersWs, [header], { origin: "A1" });
      }
      if (helperDetailsHeader != null && helperDetailsHeader.length > 0) {
        XLSX.utils.sheet_add_aoa(helperDetailsWs, [helperDetailsHeader], { origin: "A1" });
      }

      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, helpersWs, "Helpers");
      XLSX.utils.book_append_sheet(wb, helperDetailsWs, "Appointment Details");

      // Export file
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const fileExtension = ".xlsx";
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(blob, fileName + fileExtension);
    } catch (error) {
      console.error("Export failed:", error);
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>
            <Typography level={2}>Service Helpers</Typography>
          </Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder="Search ..."
              allowClear
              size="middle"
              onSearch={(val: any) => setSearchText(val)}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
      </Col>
      <Col flex={0} style={{ marginLeft: 8 }}>
        <Button
          size="middle"
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={() => handleExport()}
        >
          Export
        </Button>
      </Col>
      <Col flex={0} style={{ marginLeft: 8 }}>
        <Button type="primary" icon={<PlusCircleOutlined />} onClick={add}>
          Add
        </Button>
      </Col>
    </Row>
  );

  const helpersTable = (
    <Card title="Helpers List" style={{ marginBottom: 16 }}>
      <Form form={form} component={false}>
        <Table
          loading={saveLoading || createLoading || loading}
          showHeader={serviceHelper.length > 0}
          components={{ body: { cell: EditableCell } }}
          dataSource={serviceHelper.map((item) => ({
            key: item.id,
            ...item,
          }))}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={false}
          scroll={{ y: 400 }}
        />
      </Form>
    </Card>
  );

  return (
    <React.Fragment>
      {control}
      <Row style={{ marginTop: 8 }}>
        <Col span={24}>
          {helpersTable}
        </Col>
      </Row>
      <Row style={{ marginTop: 8 }}>
        <Col span={24}>
          {helperDetailsTable}
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default withClinic(HelperPage);
